import {
  hasListingDeferralEnded,
  getListingDeferralEndDate
} from '@strenforcement/domain/listing';

export const DEFERRAL_FILTER_ALL = 'all';
export const DEFERRAL_FILTER_ACTIVE = 'deferred';
export const DEFERRAL_FILTER_PREVIOUS = 'previously-deferred';
export const DEFERRAL_FILTER_NOT_ACTIVE = 'not-deferred';

function getRawWhitelisted(model) {
  if (typeof model?.whitelisted === 'boolean') {
    return model.whitelisted;
  }
  if (typeof model?.data?.whitelisted === 'boolean') {
    return model.data.whitelisted;
  }
  return Boolean(model?.isWhitelisted || model?.hasEndedDeferral);
}

export function getDeferralEndDate(model) {
  if (model?.deferralEndDate) {
    return model.deferralEndDate;
  }
  if (model?.whitelistExpiration) {
    return model.whitelistExpiration;
  }
  return getListingDeferralEndDate({
    whitelisted: getRawWhitelisted(model),
    whitelistExpiration: model?.data?.whitelistExpiration ?? model?.whitelistExpiration
  });
}

function toMillis(value) {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    const millis = value.getTime();
    return Number.isNaN(millis) ? null : millis;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const millis = new Date(value).getTime();
    return Number.isNaN(millis) ? null : millis;
  }
  if (typeof value.toDate === 'function') {
    return toMillis(value.toDate());
  }
  if (typeof value.valueOf === 'function') {
    const millis = value.valueOf();
    return Number.isFinite(millis) ? millis : null;
  }
  return null;
}

export function isPreviouslyDeferred(model, now = new Date()) {
  if (typeof model?.hasEndedDeferral === 'boolean') {
    return model.hasEndedDeferral;
  }
  return hasListingDeferralEnded({
    whitelisted: getRawWhitelisted(model),
    whitelistExpiration: model?.data?.whitelistExpiration ?? model?.whitelistExpiration,
    now
  });
}

export function matchesDeferralFilter(model, filterValue, now = new Date()) {
  switch (filterValue) {
    case DEFERRAL_FILTER_ACTIVE:
      return Boolean(model?.isWhitelisted);
    case DEFERRAL_FILTER_PREVIOUS:
      return isPreviouslyDeferred(model, now);
    case DEFERRAL_FILTER_NOT_ACTIVE:
      return !model?.isWhitelisted;
    case DEFERRAL_FILTER_ALL:
    default:
      return true;
  }
}

export function matchesDeferralEndedWithin(model, days, now = new Date()) {
  if (!days) {
    return true;
  }

  const endMillis = toMillis(getDeferralEndDate(model));
  if (endMillis === null) {
    return false;
  }

  const windowDays = Number(days);
  if (!Number.isFinite(windowDays) || windowDays <= 0) {
    return true;
  }

  const nowMillis = toMillis(now);
  const earliest = nowMillis - (windowDays * 24 * 60 * 60 * 1000);
  return endMillis >= earliest && endMillis <= nowMillis;
}

export function compareDeferralEndDate(a, b, ascending = true) {
  const aMillis = toMillis(getDeferralEndDate(a));
  const bMillis = toMillis(getDeferralEndDate(b));

  if (aMillis === null && bMillis === null) {
    return 0;
  }
  if (aMillis === null) {
    return 1;
  }
  if (bMillis === null) {
    return -1;
  }

  const diff = aMillis - bMillis;
  return ascending ? diff : -diff;
}
